import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/services/auth/user.service';
import { ModalService } from 'app/core/modal/modal.service';
import { ThemeService } from 'app/core/config/theme.service';
import { environment } from 'environments/environment';

@Component({
    selector: 'app-sign-in',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
})
export class SignInComponent implements OnInit {
    credentials = { username: '', password: '' };
    isLoading = false;
    currentYear = new Date().getFullYear();
    errorMessage = '';
    isCheckingAuth = true;
    showPassword: boolean = false;
    isDevMode = false;
    isSsoLoading = false;

    constructor(
        private authService: AuthService,
        private userService: UserService,
        private router: Router,
        private modalService: ModalService,
        private themeService: ThemeService,
    ) {}

    async onSsoLogin(): Promise<void> {
        this.errorMessage = '';
        this.isSsoLoading = true;

        try {
            const ssoUrl = await this.authService.getSsoRedirectUrl();
            if (ssoUrl) {
                window.location.href = ssoUrl;
            } else {
                this.errorMessage = 'Gagal mendapatkan tautan login SSO.';
                this.isSsoLoading = false;
            }
        } catch (error: any) {
            this.errorMessage = error.message || 'Gagal memulai autentikasi SSO Adhivasindo Labs.';
            this.isSsoLoading = false;
        }
    }

    ngOnInit(): void {
        // Halaman login selalu dalam mode light/brand (bebas dari dark mode)
        this.themeService.disableThemeForAuth();

        // Cek apakah sudah ada dev session atau token valid
        const devSession = localStorage.getItem('dev_session');
        const token = localStorage.getItem('a_id');
        const user = this.authService.getCurrentUser();

        if (devSession === 'true' || (token && user)) {
            if (devSession !== 'true' && !this.authService.isUserVerified()) {
                this.router.navigate(['/pending-verification'], { replaceUrl: true });
                return;
            }
            this.router.navigate(['/dashboard'], { replaceUrl: true });
            return;
        }

        // Kalau tidak valid
        localStorage.removeItem('a_id');
        localStorage.removeItem('user');
        localStorage.removeItem('dev_session');
        this.isCheckingAuth = false;
    }

    async onSignIn(): Promise<void> {
        this.errorMessage = '';

        if (
            !this.credentials.username.trim() ||
            !this.credentials.password.trim()
        ) {
            this.errorMessage = 'Masukkan username dan password';
            return;
        }

        this.isLoading = true;

        try {
            const result = await this.authService.signIn(this.credentials);

            if (result) {
                this.isCheckingAuth = false;
                if (!this.authService.isUserVerified()) {
                    this.router.navigate(['/pending-verification'], { replaceUrl: true });
                } else if (!this.authService.isProfileCompleted()) {
                    this.router.navigate(['/complete-profile'], { replaceUrl: true });
                } else {
                    this.router.navigate(['/dashboard'], { replaceUrl: true });
                }
            } else {
                this.errorMessage = 'Email atau password salah';
            }
        } catch (error: any) {
            this.errorMessage =
                error.message || 'Terjadi kesalahan saat login.';
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Dev-only: Login tanpa backend API
     * Menyimpan mock user data langsung ke localStorage
     */
    onDevLogin(): void {
        this.isLoading = true;

        const mockUser = {
            id: 'dev-001',
            name: 'Developer Admin',
            username: 'superadmin',
            email: 'dev@avia.co.id',
            role: { id: 1, name: 'Super Admin', permissions: [] },
            avatar: '',
            status: true,
            permissions: ['*'],
        };

        // Simpan sebagai plain JSON (tanpa enkripsi) untuk dev
        localStorage.setItem('dev_session', 'true');
        localStorage.setItem('user', JSON.stringify(mockUser));
        localStorage.setItem('a_id', 'dev-mock-token');

        // Set user di UserService
        this.userService.user = mockUser as any;

        setTimeout(() => {
            this.isLoading = false;
            this.router.navigate(['/dashboard'], { replaceUrl: true });
        }, 500);
    }

    /**
     * Handler untuk tombol Lupa Password
     */
    onForgotPassword(): void {
        this.modalService.alert(
            'Silakan hubungi Administrator IT atau Tim Support Adhivasindo untuk melakukan reset kata sandi akun Anda.',
            'info',
        );
    }
}
